import {
  Outlet,
  createFileRoute,
  notFound,
  useLocation,
} from '@tanstack/react-router'
import { MotionConfig } from 'framer-motion'
import { IntlProvider } from 'use-intl'
import { isLocale, type Locale } from '@/i18n/config'
import { loadMessages } from '@/i18n/translator'
// Cookie banner disabled: Umami is cookieless (CNIL audience-measurement
// exemption). Uncomment this import and the <CookieConsent /> mount below to
// re-enable — and see applyConsent() in src/lib/cookie-consent.ts to gate init.
// import { CookieConsent } from '@/components/cookie-consent'
import { Header } from '@/components/layout/header'
import { Footer } from '@/components/layout/footer'
import { sanityClient } from '@/sanity/client'
import { postsQuery } from '@/sanity/queries'
import type { FooterPost } from '@/components/layout/footer'
import { jsonLdScripts } from '@/lib/seo'
import { organizationSchema, websiteSchema } from '@/lib/structured-data'
import { LINKS_PATH } from '@/features/links/data/links'

// Latest posts (footer links + header Ressources menu) are identical across
// every page and change rarely. This layout wraps every route, so its loader
// ran a Sanity round-trip on every render —
// cold DNS+TLS on a fresh Lambda, blocking TTFB. Cache per-locale in module
// scope (survives warm invocations): only the first request on an instance pays
// the fetch. ponytail: plain TTL, add stale-while-revalidate only if a 10-min
// staleness window on footer links ever matters.
const FOOTER_TTL_MS = 10 * 60 * 1000
const footerCache = new Map<Locale, { posts: FooterPost[]; expires: number }>()

async function getFooterPosts(locale: Locale): Promise<FooterPost[]> {
  const hit = footerCache.get(locale)
  if (hit && hit.expires > Date.now()) return hit.posts
  const posts = await sanityClient
    .fetch<FooterPost[]>(postsQuery(locale))
    .then((p) => p.slice(0, 5))
    .catch(() => hit?.posts ?? []) // on failure keep serving stale, never crash the page
  footerCache.set(locale, { posts, expires: Date.now() + FOOTER_TTL_MS })
  return posts
}

export const Route = createFileRoute('/$locale')({
  beforeLoad: ({ params }) => {
    if (!isLocale(params.locale)) throw notFound()
  },
  loader: async ({ params }) => {
    const locale = params.locale as Locale
    const [messages, footerPosts] = await Promise.all([
      loadMessages(locale),
      getFooterPosts(locale),
    ])
    return { messages, footerPosts }
  },
  head: () => ({
    scripts: jsonLdScripts([organizationSchema(), websiteSchema()]),
  }),
  component: LocaleLayout,
})

function LocaleLayout() {
  const { locale } = Route.useParams()
  const { messages, footerPosts } = Route.useLoaderData()
  // Standalone pages render their own chrome (no marketing header/footer):
  // the tools, the resources and the /links "link in bio" page. The links
  // match is exact so a blog slug like "links" never loses the header.
  const isStandalone = useLocation({
    select: (l) =>
      l.pathname.includes('/tools/') ||
      l.pathname.includes('/resources/') ||
      l.pathname === `/${locale}${LINKS_PATH}`,
  })
  return (
    <IntlProvider
      locale={locale}
      messages={messages as never}
      timeZone="Europe/Paris"
    >
      {/* `reducedMotion="user"`: anyone who turned system animations off no
          longer gets the translations, only the fade remains. Also covers the
          tools, which already use framer-motion without this guard. */}
      <MotionConfig reducedMotion="user">
        {isStandalone ? (
          <Outlet />
        ) : (
          <div className="flex min-h-screen flex-col">
            <Header posts={footerPosts} />
            <main className="flex-1">
              <Outlet />
            </main>
            <Footer posts={footerPosts} />
          </div>
        )}
      </MotionConfig>
      {/* <CookieConsent /> */}
    </IntlProvider>
  )
}
